import React from 'react'
import type { ValueType } from '@rc-component/mini-decimal'
import AntdInputNumber from 'antd/es/input-number'
import type { InputNumberProps } from 'antd/es/input-number'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { InputNumber as ShadcnSeamInputNumber } from './InputNumber.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/input-number` — the twentieth
 * component put behind the design toggle, after `../Menu` (#1006) and the two
 * popup seams before it.
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it. The barrel re-exports this dispatcher, so
 * the 105 files that already write `import { InputNumber } from 'src/antd'`
 * need no import edit: every one of the app's 185 tags already resolves
 * through the seam. NO CALL SITE CHANGED ITS PROPS, ITS VALUE SHAPE OR ITS
 * BEHAVIOUR. Three call-site files ARE touched, and only for the stylesheet
 * twins the data-slot markup requires — check it against the diff:
 * `new-components/forms/FormTicketSingle/ticket/PpAddContainer/
 * PpContainerFee.tsx` and `…/ticket/PpServiceFee/index.tsx` each grew a
 * `[data-slot='…']` rule beside their `.ant-input-number` one (a styled rule
 * keyed on antd's class matches nothing once the markup is data-slot keyed),
 * and `new-components/forms/shared/externalBillingLock.tsx` is a COMMENT
 * change only.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — the shipped default, so every page load of every
 * customer who has not opted in. In the `'antd'` branch this renders antd's
 * own InputNumber with the props and the ref it was given: same element, same
 * class names, same DOM, same imperative handle, and the same value machine.
 * `../__tests__/inputnumber-seam-parity.test.tsx` asserts that across a prop
 * matrix and is the test to keep green.
 *
 * ---------------------------------------------------------------------------
 * THE ONE DELIBERATE BEHAVIOUR DIVERGENCE — READ IT BEFORE TOUCHING EITHER
 * BRANCH
 *
 * The two branches PARSE A TYPED COMMA DIFFERENTLY. On a Danish or Finnish
 * tenant the antd branch reads `375,50` as **37550** — a 100x error on money
 * fields, because rc-input-number's default parser deletes a comma it was not
 * told about — and the shadcn branch reads it as `375.5`. That asymmetry is a
 * CAPTAIN'S DECISION, not an oversight: fixing both halves was offered and
 * declined, so that what customers see today does not move mid-migration. It
 * resolves when the antd branch is deleted. `./decimalSeparator.ts` carries
 * the measurement, the reasoning and the ruling; the shape is the same one
 * `../Spin` uses for its shadcn-only `delay` default.
 *
 * WHY THIS COMPONENT. It is the app's numeric surface: 185 tags across 105
 * files, of which 33 are money. It also had no shadcn equivalent to copy —
 * shadcn's registry ships no number input and `update-design` never ported
 * one — so the value machine is transcribed from rc-input-number rather than
 * adapted from a reference, which is what `../Select` had to do with
 * rc-select's filtering. `./numeric.ts` cites every rule it copies.
 *
 * antd's one static, `_InternalPanelDoNotUseOrYouWillBeFired`, is NOT carried:
 * it is marked private by antd, has 0 call sites here, and exists only for
 * antd's own documentation site.
 * ------------------------------------------------------------------------- */
const InputNumberDispatcher = React.forwardRef<
  HTMLInputElement,
  InputNumberProps<ValueType>
>((props, ref) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn')
    return <ShadcnSeamInputNumber {...props} ref={ref} />

  return <AntdInputNumber {...props} ref={ref} />
})

InputNumberDispatcher.displayName = 'InputNumber'

// Cast back to antd's own generic signature so `<InputNumber<number> …>` —
// which one call site writes (`new-components/forms/company-settings`) — and
// every `onChange` inference keep resolving exactly as they did.
export const InputNumber = InputNumberDispatcher as unknown as (<
  T extends ValueType = ValueType
>(
  props: InputNumberProps<T> & React.RefAttributes<HTMLInputElement>
) => React.ReactElement) & { displayName?: string }
